import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { getSession, updateSession, deleteSession } from '../services/sessionService';

const { FiSave, FiTrash2, FiArrowLeft, FiPlus, FiMinus } = FiIcons;

function EditSession() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [clues, setClues] = useState([]);
  const [completionMessage, setCompletionMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    loadSession();
  }, [id]);

  const loadSession = async () => {
    try {
      const sessionData = await getSession(id);
      if (!sessionData) {
        navigate('/');
        return;
      }
      
      setSession(sessionData);
      setClues(sessionData.clues);
      setCompletionMessage(sessionData.completionMessage);
    } catch (error) {
      console.error('Error loading session:', error);
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const addClue = () => {
    setClues([...clues, { clue: '', answer: '' }]);
  };

  const removeClue = (index) => {
    if (clues.length > 1) {
      setClues(clues.filter((_, i) => i !== index));
    }
  };

  const updateClue = (index, field, value) => {
    const updated = [...clues];
    updated[index][field] = value;
    setClues(updated);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);

    const validClues = clues.filter(c => c.clue.trim() && c.answer.trim());
    
    if (validClues.length < 3) {
      alert('Bitte gib mindestens 3 vollständige Hinweis-Antwort-Paare ein.');
      setSaving(false);
      return;
    }

    try {
      const updatedSession = {
        ...session,
        clues: validClues,
        completionMessage,
        lastActivity: new Date().toISOString()
      };

      await updateSession(id, updatedSession);
      navigate(`/session/${id}`);
    } catch (error) {
      console.error('Error updating session:', error);
      alert('Fehler beim Speichern. Bitte versuche es erneut.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteSession(id);
      navigate('/');
    } catch (error) {
      console.error('Error deleting session:', error);
      alert('Fehler beim Löschen. Bitte versuche es erneut.');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-primary font-rubik">Lade Session...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white py-8">
      <div className="container mx-auto px-4 max-w-4xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <button
            onClick={() => navigate(`/session/${id}`)}
            className="flex items-center gap-2 text-primary hover:opacity-80 mb-4 font-rubik"
          >
            <SafeIcon icon={FiArrowLeft} />
            Zurück zum Rätsel
          </button>
          
          <h1 className="text-3xl font-bold text-primary mb-2 font-rubik">
            Kreuzworträtsel bearbeiten
          </h1>
          <p className="text-primary font-rubik">
            Bearbeite die Hinweise und Lösungswörter. Das Gitter wird automatisch neu generiert.
          </p>
        </motion.div>

        <motion.form
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          onSubmit={handleSave}
          className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
        >
          <div className="mb-6">
            <label className="block text-sm font-medium text-primary mb-2 font-rubik">
              Schlussnachricht
            </label>
            <input
              type="text"
              value={completionMessage}
              onChange={(e) => setCompletionMessage(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary text-primary font-rubik"
              placeholder="Nachricht bei vollständiger Lösung"
            />
          </div>

          <div className="mb-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-primary font-rubik">
                Hinweise und Lösungswörter
              </h2>
              <button
                type="button"
                onClick={addClue}
                className="flex items-center gap-2 px-4 py-2 bg-button text-white rounded-md hover:bg-button-hover transition-colors font-rubik"
              >
                <SafeIcon icon={FiPlus} />
                Hinweis hinzufügen
              </button>
            </div>

            <div className="space-y-4">
              {clues.map((clue, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="grid md:grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg"
                >
                  <div>
                    <label className="block text-sm font-medium text-primary mb-1 font-rubik">
                      Hinweis {index + 1}
                    </label>
                    <input
                      type="text"
                      value={clue.clue}
                      onChange={(e) => updateClue(index, 'clue', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary text-primary font-rubik"
                      placeholder="z.B. Hauptstadt von Deutschland"
                      required
                    />
                  </div>
                  <div className="flex gap-2">
                    <div className="flex-1">
                      <label className="block text-sm font-medium text-primary mb-1 font-rubik">
                        Lösungswort
                      </label>
                      <input
                        type="text"
                        value={clue.answer}
                        onChange={(e) => updateClue(index, 'answer', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary text-primary font-rubik"
                        placeholder="z.B. BERLIN"
                        required
                      />
                    </div>
                    {clues.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeClue(index)}
                        className="mt-6 p-2 text-red-600 hover:bg-red-50 rounded-md"
                      >
                        <SafeIcon icon={FiMinus} />
                      </button>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          <div className="flex justify-between">
            <button
              type="button"
              onClick={() => setShowDeleteConfirm(true)}
              className="flex items-center gap-2 px-6 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors font-rubik"
            >
              <SafeIcon icon={FiTrash2} />
              Session löschen
            </button>
            
            <div className="flex gap-4">
              <button
                type="button"
                onClick={() => navigate(`/session/${id}`)}
                className="px-6 py-2 border border-gray-300 text-primary rounded-md hover:bg-gray-50 transition-colors font-rubik"
              >
                Abbrechen
              </button>
              <button
                type="submit"
                disabled={saving}
                className="flex items-center gap-2 px-6 py-2 bg-button text-white rounded-md hover:bg-button-hover transition-colors disabled:opacity-50 font-rubik"
              >
                <SafeIcon icon={FiSave} />
                {saving ? 'Speichere...' : 'Änderungen speichern'}
              </button>
            </div>
          </div>
        </motion.form>

        {showDeleteConfirm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          >
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              className="bg-white rounded-lg p-6 max-w-md w-full"
            >
              <h3 className="text-lg font-semibold text-primary mb-4 font-rubik">
                Session löschen?
              </h3>
              <p className="text-primary mb-6 font-rubik">
                Diese Aktion kann nicht rückgängig gemacht werden. Die Session und alle Daten werden permanent gelöscht.
              </p>
              <div className="flex gap-4">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-primary rounded-md hover:bg-gray-50 font-rubik"
                >
                  Abbrechen
                </button>
                <button
                  onClick={handleDelete}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 font-rubik"
                >
                  Löschen
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </div>
    </div>
  );
}

export default EditSession;